import { useEffect, useCallback, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useSocketMessage, useSocketReconnectCallback } from '@reflex/ui/client/socket';
import { useOrgKey, usePluginSocket } from '@reflex/ui/client/context';
import { flowsApi } from './api.js';
import { deduplicateEvents } from '@reflex/ui/client/event-utils';
import type { AxonEvent, WsServerMessage } from '@reflex/shared';

interface FlowRunStreamResult {
  events: AxonEvent[] | undefined;
  streamIds: string[];
  isLoading: boolean;
}

export function useFlowRunStream(runId: string | undefined): FlowRunStreamResult {
  const queryClient = useQueryClient();
  const orgKey = useOrgKey();
  const socket = usePluginSocket();
  const streamQueryKey = useMemo(
    () => ['flow-run-stream', orgKey, runId] as const,
    [orgKey, runId],
  );

  const { data, isLoading } = useQuery({
    queryKey: streamQueryKey,
    queryFn: async () => {
      const result = await flowsApi.getFlowRunStream(runId!);
      return result;
    },
    enabled: !!runId,
    staleTime: Infinity,
  });

  const streamIds = useMemo(() => data?.streamIds ?? [], [data?.streamIds]);

  useEffect(() => {
    if (streamIds.length === 0) return;
    for (const id of streamIds) {
      socket.subscribe(id);
    }
    return () => {
      for (const id of streamIds) {
        socket.unsubscribe(id);
      }
    };
  }, [socket, streamIds]);

  const streamIdSet = useMemo(() => new Set(streamIds), [streamIds]);

  useSocketMessage(
    useCallback(
      (msg: WsServerMessage) => {
        if (msg.type !== 'event' || !runId) return;
        if (!streamIdSet.has(msg.event.streamId)) return;

        queryClient.setQueryData<{ events: AxonEvent[]; streamIds: string[] }>(
          streamQueryKey,
          (old) => {
            if (!old) return { events: [msg.event], streamIds: [...streamIdSet] };
            return {
              ...old,
              events: deduplicateEvents([...old.events, msg.event]),
            };
          },
        );
      },
      [runId, streamIdSet, queryClient, streamQueryKey],
    ),
  );

  const reconcileStream = useCallback(() => {
    if (!runId || streamIds.length === 0) return;
    flowsApi
      .getFlowRunStream(runId)
      .then((result) => {
        queryClient.setQueryData<{ events: AxonEvent[]; streamIds: string[] }>(
          streamQueryKey,
          (old) => {
            if (!old) return result;
            return {
              streamIds: result.streamIds,
              events: deduplicateEvents([...old.events, ...result.events]),
            };
          },
        );
      })
      .catch(() => {});
  }, [runId, streamIds, queryClient, streamQueryKey]);
  useSocketReconnectCallback(reconcileStream);

  const events = data?.events;
  const sortedEvents = useMemo(() => {
    if (!events) return undefined;
    return [...events].sort((a, b) => a.timestamp - b.timestamp);
  }, [events]);

  return { events: sortedEvents, streamIds, isLoading };
}
